import React, { useMemo } from 'react'
import { Else, If, Then } from 'react-if'
import { CircleCheckIcon } from '@/atoms/Icons/CircleCheckIcon'
import { EmptyCircleIcon } from '@/atoms/Icons/EmptyCircleIcon'
import { CaptionMD } from '@/atoms/Text'
import { slugs } from '@/constants/slugs'
import { useProjectMeta } from '@/contexts/ProjectMetaContext'
import { Offer, Perk } from '@/services/PifService'
import { getPeopleImpacted } from '@/services/PifService/PifService'
import { formatNumberForDisplay } from '@/utils/numbers'
import { Translate, useTranslate } from '@/utils/translate/translate-client'

export type ExpressPerk = { id: string; perk: Perk; isSelected?: boolean; isEarlyAccess?: boolean }
export interface TheatricalExpressAllPerkViewerProps {
  theatricalSlug: string
  projectName: string
  selectedOffer: Offer
  earlyAccessMinimumPrice?: number
  allPerks: { id: string; perk: Perk; isSelected?: boolean }[]
  handlePerkClick: (perk: ExpressPerk) => void
  isExpressCheckout?: boolean
}

// const hasEarlyAccess = (offerPrice: number, earlyAccessMinimumPrice?: number) => {
//   if (typeof earlyAccessMinimumPrice !== 'number') return false
//
//   return offerPrice >= earlyAccessMinimumPrice
// }

export const TheatricalExpressAllPerkViewer: React.FC<TheatricalExpressAllPerkViewerProps> = ({
  selectedOffer,
  allPerks,
  handlePerkClick,
  isExpressCheckout,
  theatricalSlug,
  projectName,
}) => {
  const { t } = useTranslate('pif')
  const { tributeOfferIDs } = useProjectMeta()
  const numPeopleImpacted = getPeopleImpacted(selectedOffer)
  const quantity = formatNumberForDisplay({ count: numPeopleImpacted })

  const filteredPerks = useMemo(() => {
    const filteredPerks = allPerks?.filter(({ perk }) => {
      if (perk?.product?.pif) return false
      if (tributeOfferIDs?.includes(perk?.product?.id)) return false
      return true
    })
    return filteredPerks.map((p) => {
      return { ...p, isSelected: selectedOffer.offeredproductSet.some((o) => o.product.id === p.perk.product.id) }
    })
  }, [allPerks, selectedOffer.offeredproductSet, tributeOfferIDs])

  // const hasEarlyAccessPerk = useMemo(() => {
  //   return hasEarlyAccess(getPriceForCurrency(selectedOffer.price, selectedOffer.currency), earlyAccessMinimumPrice)
  // }, [earlyAccessMinimumPrice, selectedOffer.currency, selectedOffer.price])

  // const earlyAccessPerk = useMemo(() => {
  //   const earlyAccessPerk = allPerks.find((p) => p?.perk?.product?.type === project.pifEarlyAccessPerkId)
  //   if (!earlyAccessPerk) return null
  //   return { ...earlyAccessPerk, isSelected: hasEarlyAccessPerk as boolean }
  // }, [allPerks, hasEarlyAccessPerk, project.pifEarlyAccessPerkId])
  const numPeopleImpactedPerkText = numPeopleImpacted > 1 ? `${quantity} People` : '1 Person'

  const offerDescription =
    numPeopleImpacted === 0 ? null : (
      <Translate values={{ numPeopleImpactedPerkText, projectName }} t={t} i18nKey="payitForwardForNPeoplePerkText">
        <span className="font-bold text-core-gray-950">{{ numPeopleImpactedPerkText }}</span> Can Watch{' '}
        {{ projectName }}
      </Translate>
    )
  return (
    <div className="my-6 flex flex-col gap-2">
      {filteredPerks && (
        <>
          <CaptionMD weight="bold">Your Perks and Impact</CaptionMD>
          {offerDescription && (
            <li className="mb-2 flex lg:items-start">
              <CircleCheckIcon className="mr-1.5 flex-none" color1="oxide-700" color2="white" />
              <CaptionMD>{offerDescription}</CaptionMD>
            </li>
          )}
          {(theatricalSlug === slugs.cabrini ||
            theatricalSlug === slugs.sight ||
            theatricalSlug === slugs.soundOfHopeTheStoryOfPossumTrot) &&
            isExpressCheckout && (
              <li className="mb-2 flex items-start lg:items-start">
                <CircleCheckIcon className="mr-1.5 flex-none" color1="oxide-700" color2="white" />
                <CaptionMD color="core-gray-700">
                  <If condition={theatricalSlug === slugs.soundOfHopeTheStoryOfPossumTrot}>
                    <Then>
                      <Translate t={t} i18nKey="exclusiveNewScenePerkSoundOfHopeEarlyV2">
                        <span className="font-bold text-core-gray-950">Exclusive New Scene</span> delivered by email
                        after the film is released.
                      </Translate>
                    </Then>
                    <Else>
                      <Translate t={t} i18nKey="exclusiveNewScenePerkV2">
                        <span className="font-bold text-core-gray-950">Exclusive New Scene</span> available after
                        checkout
                      </Translate>
                    </Else>
                  </If>
                </CaptionMD>
              </li>
            )}
          {/*{earlyAccessPerk && (*/}
          {/*  <li*/}
          {/*    onClick={() => earlyAccessPerk && handlePerkClick({ ...earlyAccessPerk, isEarlyAccess: true })}*/}
          {/*    className="mb-2 flex cursor-pointer items-center lg:items-start"*/}
          {/*  >*/}
          {/*    {hasEarlyAccessPerk ? (*/}
          {/*      <CircleCheckIcon className="mr-1.5 flex-none" color1="oxide-700" color2="white" />*/}
          {/*    ) : (*/}
          {/*      <EmptyCircleIcon className="mr-1.5 flex-none shrink-0" color1="core-gray-500" />*/}
          {/*    )}*/}
          {/*    <CaptionMD color={hasEarlyAccessPerk ? 'core-gray-950' : 'core-gray-500'}>*/}
          {/*      <Translate t={t} i18nKey="earlyAccessWhenItReleasesInTheApp">*/}
          {/*        <span*/}
          {/*          className={classNames(*/}
          {/*            'font-bold',*/}
          {/*            hasEarlyAccessPerk ? 'text-core-gray-950' : 'text-core-gray-500',*/}
          {/*          )}*/}
          {/*        >*/}
          {/*          Early Access*/}
          {/*        </span>{' '}*/}
          {/*        when it releases in the app*/}
          {/*      </Translate>*/}
          {/*    </CaptionMD>*/}
          {/*  </li>*/}
          {/*)}*/}
          {filteredPerks.map(({ perk, isSelected, id }) => {
            let perkText = perk.name

            if (perk?.product?.pif && perk?.quantity) {
              perkText = t('payItForwardForPeople', {
                count: perk.quantity,
                defaultValue_one: '{{quantity}} person can watch {{projectName}} for free',
                defaultValue_other: '{{quantity}} people can watch {{projectName}} for free',
                projectName,
                quantity: formatNumberForDisplay({ count: perk.quantity, format: '0[.]0a' }),
              })
            }
            return (
              <div
                onClick={() => {
                  handlePerkClick({ perk, isSelected, id })
                }}
                key={perk?.id}
                className="flex cursor-pointer items-start gap-2"
              >
                <div className="my-0.5 h-4 w-4">
                  {isSelected ? (
                    <CircleCheckIcon className="mr-1.5 flex-none" color1="oxide-700" color2="white" />
                  ) : (
                    <EmptyCircleIcon className="shrink-0" color1="core-gray-500" />
                  )}
                </div>
                <CaptionMD color={isSelected ? 'core-gray-950' : 'core-gray-500'} className="my-0">
                  {perkText}
                </CaptionMD>
              </div>
            )
          })}
        </>
      )}
    </div>
  )
}
